import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Loader2, Copy, Check, Shield } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface MFASetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function MFASetupDialog({ open, onOpenChange, onSuccess }: MFASetupDialogProps) {
  const { enrollMFA, verifyMFAEnrollment } = useAuth();
  const [step, setStep] = useState<'enroll' | 'verify'>('enroll');
  const [qrCode, setQrCode] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const handleEnroll = async () => {
    setIsLoading(true);
    try {
      const { error, qrCode: qr, secret: sec } = await enrollMFA();
      
      if (error) throw error;
      
      setQrCode(qr || "");
      setSecret(sec || "");
      setStep('verify');
      
      toast({
        title: "MFA Enrollment Started",
        description: "Scan the QR code with your authenticator app",
      });
    } catch (error: any) {
      console.error('MFA enrollment error:', error);
      toast({
        variant: "destructive",
        title: "Enrollment Failed",
        description: error.message || "Failed to start MFA enrollment",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        variant: "destructive",
        title: "Invalid Code",
        description: "Please enter a 6-digit code",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await verifyMFAEnrollment(verificationCode);
      
      if (error) throw error;
      
      toast({
        title: "2-Step Verification Enabled",
        description: "Your account is now protected with 2-step verification",
      });
      
      onSuccess?.();
      onOpenChange(false);
      
      // Reset state
      setStep('enroll');
      setQrCode("");
      setSecret("");
      setVerificationCode("");
    } catch (error: any) {
      console.error('MFA verification error:', error);
      toast({
        variant: "destructive",
        title: "Verification Failed",
        description: error.message || "Invalid verification code",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copySecret = async () => {
    await navigator.clipboard.writeText(secret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
    toast({
      title: "Secret Copied",
      description: "Secret key copied to clipboard",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <DialogTitle>Enable 2-Step Verification</DialogTitle>
          </div>
          <DialogDescription>
            {step === 'enroll' 
              ? "Add an extra layer of security to your account"
              : "Enter the 6-digit code from your authenticator app"
            }
          </DialogDescription>
        </DialogHeader>

        {step === 'enroll' ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                You'll need an authenticator app like Google Authenticator, Authy, or 1Password to set up 2-step verification.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                After clicking "Start Setup", you'll receive a QR code to scan with your authenticator app.
              </p>
            </div>

            <Button 
              onClick={handleEnroll} 
              disabled={isLoading}
              className="w-full"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Start Setup
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Scan QR Code</Label>
              <div className="flex justify-center p-4 bg-white rounded-lg">
                {qrCode && <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Or enter this secret key manually</Label>
              <div className="flex gap-2">
                <Input 
                  value={secret}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copySecret}
                >
                  {copiedSecret ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="verification-code">Verification Code</Label>
              <Input
                id="verification-code"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl tracking-widest"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('enroll');
                  setQrCode("");
                  setSecret("");
                  setVerificationCode("");
                }}
                className="flex-1"
              >
                Back
              </Button>
              <Button 
                onClick={handleVerify}
                disabled={isLoading || verificationCode.length !== 6}
                className="flex-1"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify & Enable
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
